import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { pageTransition, formatDate } from "@/lib/utils";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SkeletonCard } from "@/components/ui/Skeleton";
import { useToast } from "@/hooks/useToast";
import { ArrowLeft, CheckCircle2, Plus, XCircle } from "lucide-react";
import {
  addCustomTask,
  cancelInstance,
  getInstance,
  listInstanceTasks,
  toggleTask,
  updateInstance,
  type LifecycleInstance,
  type LifecycleTask,
} from "@/api/lifecycle";
import { getEmployee } from "@/api/employees";
import type { Employee } from "@/types/employee";

const CATEGORY_LABELS: Record<string, string> = {
  ACCESS: "Access & Security",
  EQUIPMENT: "Equipment",
  KNOWLEDGE: "Knowledge Transfer",
  BENEFITS: "Benefits",
  FINAL_PAY: "Final Pay",
  EXIT: "Exit",
  PAPERWORK: "Paperwork",
  TRAINING: "Training",
  INTRODUCTION: "Introductions",
  ADMIN: "Administrative",
  OTHER: "Other",
};

// Tasks are surfaced from the Task table which doesn't carry a category
// column in the slim schema — group by a best-effort keyword scan of the
// title. Works for the seeded default template.
function guessCategory(title: string): string {
  const t = title.toLowerCase();
  if (/access|email|slack|teams|badge|credential|password|sso/.test(t))
    return "ACCESS";
  if (/laptop|phone|equipment|asset|key|card/.test(t)) return "EQUIPMENT";
  if (/knowledge|handoff|hand off|transfer|document|reassign/.test(t))
    return "KNOWLEDGE";
  if (/benefit|cobra|pto|payroll|paycheck|pay/.test(t)) return "BENEFITS";
  if (/exit|interview|survey|reference/.test(t)) return "EXIT";
  return "ADMIN";
}

export function OffboardingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [instance, setInstance] = useState<LifecycleInstance | null>(null);
  const [tasks, setTasks] = useState<LifecycleTask[] | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [busyTaskIds, setBusyTaskIds] = useState<Set<string>>(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDue, setNewDue] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const inst = await getInstance(id);
        if (cancelled) return;
        setInstance(inst);
        const [taskRows, emp] = await Promise.all([
          listInstanceTasks(id),
          getEmployee(inst.employee_id).catch(() => null),
        ]);
        if (cancelled) return;
        setTasks(taskRows);
        setEmployee(emp);
      } catch {
        addToast("error", "Failed to load offboarding");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, addToast]);

  const grouped = useMemo(() => {
    const out: Record<string, LifecycleTask[]> = {};
    for (const t of tasks || []) {
      const c = guessCategory(t.title);
      (out[c] ||= []).push(t);
    }
    return out;
  }, [tasks]);

  const handleToggle = async (task: LifecycleTask) => {
    if (!id) return;
    setBusyTaskIds((s) => new Set(s).add(task.id));
    const wantComplete = task.status !== "DONE";
    try {
      const updated = await toggleTask(id, task.id, wantComplete);
      setTasks((cur) =>
        cur ? cur.map((t) => (t.id === task.id ? updated : t)) : cur
      );
      // Refresh instance so the progress counter stays correct.
      setInstance(await getInstance(id));
    } catch {
      addToast("error", "Could not update task");
    } finally {
      setBusyTaskIds((s) => {
        const next = new Set(s);
        next.delete(task.id);
        return next;
      });
    }
  };

  const handleAdd = async () => {
    if (!id || !newTitle.trim()) return;
    setAdding(true);
    try {
      const created = await addCustomTask(id, {
        title: newTitle.trim(),
        due_date: newDue || undefined,
      });
      setTasks((cur) => (cur ? [...cur, created] : [created]));
      setInstance(await getInstance(id));
      setNewTitle("");
      setNewDue("");
      setShowAdd(false);
    } catch {
      addToast("error", "Could not add task");
    } finally {
      setAdding(false);
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    try {
      await updateInstance(id, { status: "COMPLETED" });
      setInstance(await getInstance(id));
      addToast("success", "Offboarding marked complete");
    } catch {
      addToast("error", "Could not complete offboarding");
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    if (!confirm("Cancel this offboarding? Tasks will stop appearing on dashboards."))
      return;
    try {
      await cancelInstance(id);
      addToast("success", "Offboarding cancelled");
      navigate("/offboarding");
    } catch {
      addToast("error", "Could not cancel");
    }
  };

  if (!instance || !tasks) {
    return (
      <div className="space-y-3">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  const pct =
    instance.total_tasks === 0
      ? 0
      : Math.round((instance.completed_tasks / instance.total_tasks) * 100);
  const isActive = instance.status === "IN_PROGRESS";
  const today = new Date().toISOString().slice(0, 10);

  return (
    <motion.div {...pageTransition}>
      <button
        onClick={() => navigate("/offboarding")}
        className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to offboarding
      </button>

      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          {employee && (
            <Avatar
              src={employee.avatar_url}
              firstName={employee.first_name}
              lastName={employee.last_name}
              size="xl"
            />
          )}
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold font-heading text-text-primary">
                  {employee
                    ? `${employee.first_name} ${employee.last_name}`
                    : "Employee"}
                </h2>
                <p className="text-text-muted mt-0.5">
                  {employee?.title || ""}
                </p>
                <div className="flex items-center gap-3 mt-2 text-sm">
                  <Badge
                    variant={
                      instance.status === "IN_PROGRESS"
                        ? "warning"
                        : instance.status === "COMPLETED"
                        ? "success"
                        : "default"
                    }
                  >
                    {instance.status.replace("_", " ")}
                  </Badge>
                  {employee?.termination_date && (
                    <span className="text-text-muted">
                      Last day: {formatDate(employee.termination_date)}
                    </span>
                  )}
                  {employee?.termination_reason && (
                    <span className="text-text-muted">
                      · {employee.termination_reason.replace(/_/g, " ")}
                    </span>
                  )}
                </div>
              </div>
              {isActive && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleComplete}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Complete
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleCancel}>
                    <XCircle className="h-4 w-4" />
                    Cancel
                  </Button>
                </div>
              )}
            </div>

            <div className="mt-4">
              <div className="flex justify-between text-xs text-text-muted mb-1">
                <span>
                  {instance.completed_tasks} of {instance.total_tasks} tasks
                  completed
                </span>
                <span>{pct}%</span>
              </div>
              <div className="h-2 bg-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {Object.keys(grouped)
        .sort()
        .map((cat) => (
          <Card key={cat} className="mb-4">
            <CardHeader>
              <CardTitle>{CATEGORY_LABELS[cat] || cat}</CardTitle>
            </CardHeader>
            <div className="space-y-1">
              {grouped[cat].map((task) => {
                const done = task.status === "DONE";
                const overdue =
                  !done && task.due_date && task.due_date < today;
                return (
                  <label
                    key={task.id}
                    className="flex items-start gap-3 py-2 px-1 hover:bg-surface-hover rounded-md cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={done}
                      disabled={busyTaskIds.has(task.id) || !isActive}
                      onChange={() => handleToggle(task)}
                      className="mt-1 rounded border-border text-accent focus:ring-accent"
                    />
                    <div className="flex-1">
                      <p
                        className={`text-sm ${
                          done
                            ? "text-text-muted line-through"
                            : "text-text-primary"
                        }`}
                      >
                        {task.title}
                      </p>
                      {task.description && (
                        <p className="text-xs text-text-muted mt-0.5">
                          {task.description}
                        </p>
                      )}
                      {task.due_date && (
                        <span
                          className={`text-xs ${
                            overdue ? "text-danger" : "text-text-muted"
                          }`}
                        >
                          Due {formatDate(task.due_date)}
                          {overdue ? " · overdue" : ""}
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </Card>
        ))}

      {isActive && (
        <Card>
          {!showAdd ? (
            <Button variant="ghost" size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="h-4 w-4" />
              Add task
            </Button>
          ) : (
            <div className="flex gap-2">
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Task title"
                className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
              />
              <input
                type="date"
                value={newDue}
                onChange={(e) => setNewDue(e.target.value)}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              />
              <Button
                size="sm"
                onClick={handleAdd}
                isLoading={adding}
                disabled={!newTitle.trim()}
              >
                Add
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowAdd(false);
                  setNewTitle("");
                  setNewDue("");
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </Card>
      )}
    </motion.div>
  );
}
